import React, { useState, useEffect } from 'react'
import Combobox            from '@shared/combobox'
import ComboboxMulti       from '@shared/combobox_multi'
import { getRequest }      from '@react/helpers/fetch'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const newSection        = () => ({ key: crypto.randomUUID(), id: null, name: null, availableSites: [], sitesLoaded: true, selectedSites: [] })
const fetchClientSites  = id => getRequest(`/clients/${id}/build.json?skip_contacts=true`)

const ContactLinks = ({ clients }) => {
  const [sections, setSections] = useState(() => {
    if (clients?.length > 0) {
      return clients.map(client => ({
        key:           client.id,
        id:            client.id,
        name:          client.name,
        availableSites: [],
        sitesLoaded:   false,
        selectedSites: client.sites.map(id => ({ id, name: null }))
      }))
    }
    return [newSection()]
  })

  useEffect(() => {
    sections.forEach(({ id }) => {
      if (!id) return
      fetchClientSites(id).then(({ sites = [] }) => {
        setSections(prev => prev.map(section => {
          if (section.id !== id) return section
          const resolved = section.selectedSites.map(row => sites.find(s => s.id === row.id) ?? row)
          return { ...section, availableSites: sites, sitesLoaded: true, selectedSites: resolved }
        }))
      })
    })
  }, [])

  const update        = (key, changes) => setSections(prev => prev.map(s => s.key === key ? { ...s, ...changes } : s))
  const addSection    = ()  => setSections(prev => [...prev, newSection()])
  const removeSection = key => setSections(prev => prev.filter(s => s.key !== key))

  const selectClient = async (sectionKey, client) => {
    if (!client) { update(sectionKey, newSection()); return }
    const { sites = [] } = await fetchClientSites(client.id)
    update(sectionKey, { id: client.id, name: client.name, availableSites: sites, sitesLoaded: true, selectedSites: [] })
  }

  const selectedClientIds = sections.filter(s => s.id !== null).map(s => s.id)

  return (
    <div>
      {sections.map((section, index) => (
        <ClientSection
          key            = {section.key}
          section        = {section}
          isFirst        = {index === 0}
          canRemove      = {sections.length > 1}
          excludeIds     = {selectedClientIds.filter(id => id !== section.id)}
          onSelectClient = {client => selectClient(section.key, client)}
          onRemove       = {() => removeSection(section.key)}
          onSelectSites  = {sites => update(section.key, { selectedSites: sites })}
        />
      ))}

      <div className="d-flex justify-content-end mt-2">
        <a href="#" className="small" onClick={e => { e.preventDefault(); addSection() }}>
          <i className="fa fa-plus-circle mr-1" />
          {I18n.t('contacts.add_client')}
        </a>
      </div>
    </div>
  )
}

const ClientSection = ({ section, isFirst, canRemove, excludeIds, onSelectClient, onRemove, onSelectSites }) => {
  const excludeParams = excludeIds.length > 0 ? '?' + excludeIds.map(id => `exclude[]=${id}`).join('&') : ''
  const clientFeed    = `/clients/autocomplete.json${excludeParams}`

  return (
  <div className={`mb-3${isFirst ? '' : ' border-top pt-3'}`}>

    {section.id && (
      section.selectedSites.length > 0
        ? section.selectedSites.map(site => (
            <React.Fragment key={site.id}>
              <input type="hidden" name="contact[client_sites][][client_id]" value={section.id} readOnly />
              <input type="hidden" name="contact[client_sites][][site_id]"   value={site.id}    readOnly />
            </React.Fragment>
          ))
        : <>
            <input type="hidden" name="contact[client_sites][][client_id]" value={section.id} readOnly />
            <input type="hidden" name="contact[client_sites][][site_id]"   value=""           readOnly />
          </>
    )}

    <div className="form-group mb-2">
      <label>{I18n.t('client.client')}</label>
      <div className="d-flex align-items-center">
        <div className="flex-grow-1">
          <Combobox
            feed            = {clientFeed}
            value           = {section.id}
            options         = {section.id ? [{ id: section.id, name: section.name }] : []}
            placeholder     = {I18n.t('workorders.select_client')}
            required        = {true}
            onSelectElement = {onSelectClient}
            onReset         = {() => onSelectClient(null)}
            resetIcon
          />
        </div>
        <a href="#" className="minus-circle ml-2 mr-0" onClick={e => { e.preventDefault(); canRemove ? onRemove() : onSelectClient(null) }}>
          <FontAwesomeIcon icon="minus-circle" />
        </a>
      </div>
    </div>

    {section.id && section.sitesLoaded && (
      <div className="form-group">
        <label>{I18n.t('sites.sites')}</label>
        <ComboboxMulti
          options  = {section.availableSites}
          value    = {section.selectedSites}
          prompt   = {I18n.t('workorders.select_site')}
          onChange = {onSelectSites}
        />
      </div>
    )}
  </div>
  )
}

export default ContactLinks
